import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../../context/AuthContext";
import { fetchWithAuth } from "../../../../utils/api";
import { swalError } from "../../../../utils/swalAlert";
import Loader from "../../../ui/Loader";
import "../../../../App.css";

const ExamInstructions = () => {
  const { testId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [examStarted, setExamStarted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(
    () => !!(document.fullscreenElement || document.webkitFullscreenElement)
  );

  // PIN modal state
  const [showPinModal, setShowPinModal] = useState(false);
  const [examPin, setExamPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [isVerifyingPin, setIsVerifyingPin] = useState(false);

  const countdownTimerRef = useRef(null);

  /* ── Fullscreen helpers ────────────────────────────────────────────── */
  const requestFullscreen = useCallback(() => {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      setIsFullscreen(true);
      return;
    }
    const el = document.documentElement;
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document);
    const hasFullscreenAPI =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen;

    if (!hasFullscreenAPI || isIOS) {
      setIsFullscreen(true);
      return;
    }
    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => setIsFullscreen(true));
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen();
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const isFs = !!(
        document.fullscreenElement || document.webkitFullscreenElement
      );
      setIsFullscreen(isFs);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);

    // Ensure state matches DOM on mount (in case it changed right after initial render)
    const initialFs = !!(
      document.fullscreenElement || document.webkitFullscreenElement
    );
    setIsFullscreen(initialFs);

    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  /* ── Fetch Test Details ──────────────────────────────────────────────── */
  useEffect(() => {
    const fetchTest = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/test/get/${testId}`,
          { credentials: "include" },
        );
        if (!response.ok) throw new Error("Failed to load test details.");
        const data = await response.json();

        // Check if exam window is closed
        if (data.dueDate && new Date() > new Date(data.dueDate)) {
          swalError("This exam window has closed.");
          navigate(-1);
          return;
        }

        // Check if already submitted
        try {
          const submissionRes = await fetchWithAuth(
            `${serverUrl}/api/answer-paper/get/test/${data._id}`,
            { credentials: "include" },
          );
          if (submissionRes.ok) {
            const submissionData = await submissionRes.json();
            if (submissionData && Object.keys(submissionData).length > 0) {
              if (submissionData.status !== "Pending") {
                swalError("You have already submitted this exam.");
                navigate(-1);
                return;
              }
            }
          }
        } catch {
          // No submission found, continue
        }

        setTest(data);

        // Check if exam has started
        if (data.scheduledDate) {
          const scheduledTime = new Date(data.scheduledDate).getTime();
          const now = Date.now();
          if (now >= scheduledTime) {
            setExamStarted(true);
          }
        } else {
          // No scheduled date means it's open
          setExamStarted(true);
        }
      } catch (err) {
        swalError(err.message);
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    if (user && testId) fetchTest();
  }, [testId, user, serverUrl, navigate]);

  /* ── Countdown Timer ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!test?.scheduledDate) return;

    const scheduledTime = new Date(test.scheduledDate).getTime();

    const updateCountdown = () => {
      const now = Date.now();
      const distance = scheduledTime - now;

      if (distance <= 0) {
        setExamStarted(true);
        setCountdown(null);
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds, total: distance });
    };

    updateCountdown();
    countdownTimerRef.current = setInterval(updateCountdown, 1000);

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, [test]);

  /* ── DevTools Detection & Freezer ──────────────────────────────────── */
  useEffect(() => {
    if (process.env.REACT_APP_DISABLE_DEBUGGER_VALIDATION === "true") {
      return;
    }
    const devtoolsInterval = setInterval(() => {
      const before = performance.now();
      // eslint-disable-next-line no-new-func
      (new Function("debugger"))();
      const after = performance.now();
      // If debugger paused execution, it took > 100ms
      if (after - before > 100) {
        // DevTools detected — silently block (no violation log on instructions page)
      }
    }, 1000);
    return () => clearInterval(devtoolsInterval);
  }, []);

  /* ── Block dangerous keyboard shortcuts ────────────────────────────── */
  useEffect(() => {
    const onKeyDown = (e) => {
      // Block F5 / Ctrl+R (refresh)
      if (
        e.key === "F5" ||
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "r")
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // Block F12 / Ctrl+Shift+I/J/C (DevTools)
      if (
        e.key === "F12" ||
        ((e.ctrlKey || e.metaKey) &&
          e.shiftKey &&
          ["i", "j", "c"].includes(e.key.toLowerCase()))
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // Block Ctrl+U (view source)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "u") {
        e.preventDefault();
        return false;
      }
      // Block Ctrl+P (print)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        return false;
      }
      // Block Ctrl+S (save)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        return false;
      }
      // Block Ctrl+W / Ctrl+F4 (close tab)
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key.toLowerCase() === "w" || e.key === "F4")
      ) {
        e.preventDefault();
        return false;
      }
      // Block Alt+F4 (close window)
      if (e.altKey && e.key === "F4") {
        e.preventDefault();
        return false;
      }
      // Block PrintScreen
      if (e.key === "PrintScreen") {
        e.preventDefault();
        navigator.clipboard.writeText("").catch(() => {});
        return false;
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  /* ── Block context menu, copy, cut, paste, drag, select ────────────── */
  useEffect(() => {
    const block = (e) => e.preventDefault();
    document.addEventListener("contextmenu", block);
    document.addEventListener("copy", block);
    document.addEventListener("cut", block);
    document.addEventListener("paste", block);
    document.addEventListener("dragstart", block);
    document.addEventListener("drop", block);
    document.addEventListener("selectstart", block);
    return () => {
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("copy", block);
      document.removeEventListener("cut", block);
      document.removeEventListener("paste", block);
      document.removeEventListener("dragstart", block);
      document.removeEventListener("drop", block);
      document.removeEventListener("selectstart", block);
    };
  }, []);

  /* ── Beforeunload ──────────────────────────────────────────────────── */
  useEffect(() => {
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  /* ── Format Date ─────────────────────────────────────────────────────── */
  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  }, []);

  /* ── Start Exam Handler ──────────────────────────────────────────────── */
  const handleStartExam = useCallback(async () => {
    if (!test) return;

    // Server-side time verification to prevent device clock manipulation
    try {
      const timeRes = await fetchWithAuth(
        `${serverUrl}/api/test/verify-start-time?testScheduledDate=${test.scheduledDate}`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (!timeRes.ok) {
        const errData = await timeRes.json();
        swalError(errData.message || "Cannot start exam at this time.");
        return;
      }
    } catch {
      swalError("Unable to verify exam start time. Please check your internet connection.");
      return;
    }

    // If the test has a passcode, show PIN modal
    if (test.onlineExamPasscode) {
      setShowPinModal(true);
      setExamPin("");
      setPinError("");
      return;
    }

    // No passcode, navigate directly to exam
    navigate(`/student/exam/${test._id}`, { state: { testData: test } });
  }, [test, navigate, serverUrl]);

  /* ── Verify PIN Handler ──────────────────────────────────────────────── */
  const handleVerifyPin = async (e) => {
    e.preventDefault();
    if (!examPin || examPin.length !== 4) {
      setPinError("Please enter a 4-digit PIN.");
      return;
    }

    setIsVerifyingPin(true);
    setPinError("");

    try {
      const response = await fetch(`${serverUrl}/api/test/verify-passcode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          testId: test._id,
          passcode: examPin,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Invalid PIN");
      }

      setShowPinModal(false);
      navigate(`/student/exam/${test._id}`, { state: { testData: test } });
    } catch (err) {
      setPinError(err.message);
    } finally {
      setIsVerifyingPin(false);
    }
  };

  /* ── Can Start ───────────────────────────────────────────────────────── */
  const canStart = examStarted && agreedToTerms;

  const handleGoBack = useCallback(() => {
    if (currentStep === 1) {
      exitFullscreen();
      navigate(-1);
    } else {
      setCurrentStep((p) => p - 1);
    }
  }, [currentStep, exitFullscreen, navigate]);

  /* ── Loading / Error ─────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="ei-loading">
        <Loader style={{ left: "0", top: "0" }} />
        <p>Loading exam instructions...</p>
      </div>
    );
  }

  if (!test) return null;

  const totalQuestions =
    test.onlineExamQuestions?.length || test.totalQuestions || 0;
  const roundVal = (v) => {
    if (v == null || v === "") return v;
    const n = typeof v === "number" ? v : parseFloat(v);
    return isNaN(n) ? v : Math.round((n + Number.EPSILON) * 100) / 100;
  };
  const totalMarks =
    test.maxScore != null && test.maxScore !== ""
      ? roundVal(test.maxScore)
      : (test.onlineExamQuestions || []).reduce(
          (sum, q) => sum + (parseFloat(q.maxScore) || 0),
          0
        );
  const duration = test.duration ? roundVal(test.duration) : null;
  const marksPerQuestion =
    test.onlineExamQuestions?.[0]?.maxScore != null
      ? roundVal(test.onlineExamQuestions[0].maxScore)
      : totalQuestions > 0 && totalMarks > 0
      ? roundVal(totalMarks / totalQuestions)
      : null;

  /* ── Check if Udghosh category ───────────────────────────────────────── */
  const isUdghosh =
    user?.category === "UDGHOSH" ||
    user?.institute?.category === "UDGHOSH" ||
    test?.institute?.category === "UDGHOSH";
  const totalSteps = isUdghosh ? 3 : 4;

  /* ── Udghosh Sections & Marking Scheme ───────────────────────────────── */
  const udghoshSections = [
    {
      name: "Quantitative Aptitude",
      range: "Questions 1–12",
      icon: "fa-calculator",
      questions: 12,
      marks: 48,
      correct: "+4",
      incorrect: "-2",
      accentColor: "#f09a21",
    },
    {
      name: "Logical Reasoning",
      range: "Questions 13–24",
      icon: "fa-brain",
      questions: 12,
      marks: 48,
      correct: "+4",
      incorrect: "-2",
      accentColor: "#0072bb",
    },
    {
      name: "Verbal Ability",
      range: "Questions 25–32",
      icon: "fa-book-open-reader",
      questions: 8,
      marks: 32,
      correct: "+4",
      incorrect: "-1",
      accentColor: "#8b5cf6",
    },
    {
      name: "Sports Trivia",
      range: "Questions 33–40",
      icon: "fa-trophy",
      questions: 8,
      marks: 32,
      correct: "+4",
      incorrect: "-1",
      accentColor: "#8dc63f",
    },
  ];

  /* ── Derive configuration from test data ────────────────────────────── */
  const advOptions = test.advancedOnlineExamOptions?.[0] || {};
  const isAdvancedExam = !!(test.advancedOnlineExamOptions && test.advancedOnlineExamOptions.length > 0);
  const hasVideoProctoring = !!advOptions.videoProctoring || isUdghosh;
  const canNavigateBack = advOptions.navigationBackToQuestion !== false && advOptions.navigationBackToQuestion !== "false";
  const hasShuffleQuestions = !!advOptions.shuffleQuestions;
  const hasShuffleOptions = !!advOptions.shuffleOptions;
  const hasCalculator = !!test.isCalculatorAllowed;
  const hasPasscode = !!test.onlineExamPasscode;
  const hasPerQuestionTimer = isAdvancedExam && (test.onlineExamQuestions || []).some(
    (q) => q.duration != null && q.duration !== "" && q.duration !== 0
  );
  const hasResultsImmediately = !!test.scorePublishedDate;

  /* ── Udghosh Categorized Rules ──────────────────────────────────────── */
  const udghoshRuleCategories = [
    {
      id: "proctoring",
      title: "Proctoring Rules",
      icon: "fa-video",
      colorClass: "danger",
      rules: [
        "Camera permission is mandatory to start the examination.",
        "Your face must remain clearly visible and centred in the camera throughout the exam.",
        "Keep all notifications and pop-ups silent. Do Not Disturb mode is recommended.",
        "Stay on the examination portal for the entire exam.",
      ],
    },
    {
      id: "sections-questions",
      title: "Section & Question Rules",
      icon: "fa-layer-group",
      colorClass: "info",
      rules: [
        "There are 4 sections, and switching between sections is not allowed.",
        "You must complete the current section before moving to the next section.",
        "Once you move to the next section, you cannot return to the previous section.",
        ...(canNavigateBack
          ? [
              "Within a section, you can move between questions and return to unanswered questions.",
              "However, once you attempt and submit an answer to a question, you cannot re-attempt or change that answer again.",
            ]
          : [
              "You cannot go back to a previous question once you move forward. Make sure your answer is final before proceeding.",
            ]),
        "Please read each question carefully before submitting your answer.",
      ],
    },
    {
      id: "tab-switching",
      title: "Tab Switching & Unfair Means",
      icon: "fa-triangle-exclamation",
      colorClass: "warning",
      rules: [
        "Tab switching or using any unfair means will be treated as a violation.",
        "Every violation will be carefully monitored and reviewed after the examination.",
        "If you are found guilty of breaking the examination rules, you will be disqualified.",
        "These rules are in place to ensure a fair and equal examination for everyone.",
      ],
    },
  ];

  /* ── Countdown formatted ─────────────────────────────────────────────── */
  const countdownText = countdown
    ? `${countdown.days > 0 ? `${countdown.days}d ` : ""}${String(countdown.hours).padStart(2, "0")}:${String(countdown.minutes).padStart(2, "0")}:${String(countdown.seconds).padStart(2, "0")}`
    : null;

  /* ── Build dynamic rules ─────────────────────────────────────────────── */
  const rules = [
    { icon: "fa-expand", color: "warning", title: "Fullscreen Mode is Mandatory", text: "The exam is locked in Fullscreen Mode. Attempting to exit or bypass this mode is a severe violation and may lead to immediate exam termination." },
    { icon: "fa-triangle-exclamation", color: "danger", title: "Browser Lock Enabled", text: "Navigating away from the exam window is strictly prohibited. Any attempt to switch tabs, minimize the browser, or open other applications will be flagged as malpractice." },
    { icon: "fa-ban", color: "warning", title: "Restricted Actions", text: "All keyboard shortcuts, copying, pasting, and right-clicking are disabled to maintain exam integrity." },
  ];

  // Video proctoring rule
  if (hasVideoProctoring) {
    rules.push({ icon: "fa-video", color: "danger", title: "Strict Identity & Environment Monitoring", text: "Your live feed is continuously analyzed. Any presence of unauthorized persons or suspicious behavior will result in instant disqualification." });
  }

  // Timer rule
  if (duration && hasPerQuestionTimer) {
    rules.push({ icon: "fa-clock", color: "info", title: "Timed Exam — Per Question & Overall", text: `You have ${duration} minutes overall. Each question also has its own individual timer. When a question's time runs out, it auto-advances to the next question.` });
  } else if (duration) {
    rules.push({ icon: "fa-clock", color: "info", title: "Timer & Exam Freeze", text: `You have ${duration} minutes to complete this exam. When time runs out, the exam will freeze and you must manually submit it.` });
  } else if (hasPerQuestionTimer) {
    rules.push({ icon: "fa-clock", color: "info", title: "Per-Question Timer", text: "Each question has its own individual timer. When a question's time runs out, it auto-advances to the next question." });
  }

  // Navigation rule
  if (canNavigateBack) {
    rules.push({ icon: "fa-rotate", color: "info", title: "You Can Revisit Questions", text: "Navigate freely between questions and change answers before submission." });
  } else {
    rules.push({ icon: "fa-arrow-right", color: "warning", title: "No Going Back", text: "You cannot go back to a previous question once you move forward. Make sure your answer is final before proceeding." });
  }

  // Shuffle rules
  if (hasShuffleQuestions || hasShuffleOptions) {
    const shuffleDesc = hasShuffleQuestions && hasShuffleOptions
      ? "Both questions and options are randomized for each student."
      : hasShuffleQuestions
        ? "Questions are presented in a randomized order for each student."
        : "Options within each question are randomized for each student.";
    rules.push({ icon: "fa-shuffle", color: "info", title: "Randomized Exam", text: shuffleDesc });
  }

  // Calculator rule
  if (hasCalculator) {
    rules.push({ icon: "fa-calculator", color: "info", title: "Calculator Available", text: "An on-screen calculator is available from the sidebar during the exam." });
  }

  // Results rule
  if (hasResultsImmediately) {
    rules.push({ icon: "fa-chart-column", color: "info", title: "Results Available Immediately", text: "Your score and detailed results will be shown immediately after submission." });
  }

  // Passcode rule
  if (hasPasscode) {
    rules.push({ icon: "fa-key", color: "warning", title: "Exam PIN Required", text: "A 4-digit PIN from your instructor is required to start the exam." });
  }

  // Integrity rule (always last)
  rules.push({ icon: "fa-gavel", color: "danger", title: "Zero Tolerance Policy", text: `This is a highly secure exam environment. A zero-tolerance policy applies to any form of cheating, unfair means, or use of unauthorized aids.` });

  /* ── Build navigation guide items ────────────────────────────────────── */
  const navGuideItems = [
    { cls: "save-next", label: "Save & Next", icon: "fa-chevron-right", iconPos: "right", desc: "Saves answer, moves to next." },
    { cls: "clear-response", label: "Clear Response", icon: "fa-eraser", iconPos: "left", desc: "Removes your selected answer." },
  ];
  if (canNavigateBack) {
    navGuideItems.push({ cls: "mark-review", label: "Mark for Review", icon: "fa-bookmark", iconPos: "left", desc: "Flags question for review." });
    navGuideItems.push({ cls: "previous", label: "Previous", icon: "fa-chevron-left", iconPos: "left", desc: "Goes to previous question." });
  }

  /* ── Tabs List ───────────────────────────────────────────────────────── */
  const tabs = isUdghosh
    ? [
        { num: 1, label: "Exam Details", icon: "fa-circle-info" },
        { num: 2, label: "Status Guide", icon: "fa-palette" },
        { num: 3, label: "Rules & Agreement", icon: "fa-gavel" },
      ]
    : [
        { num: 1, label: "Exam Details", icon: "fa-circle-info" },
        { num: 2, label: "Status Guide", icon: "fa-palette" },
        { num: 3, label: "Rules", icon: "fa-gavel" },
        { num: 4, label: "Agreement", icon: "fa-file-signature" },
      ];

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <div className="ei-page notranslate" translate="no">
      {/* ── Fullscreen Gate ──────────────────────────────────────── */}
      {!isFullscreen && (
        <div className="exam-fullscreen-overlay">
          <div className="exam-syscheck-card">
            <i className="fa-solid fa-expand" style={{ fontSize: "3rem", color: "var(--color-1)", marginBottom: "0.5rem" }} />
            <h2>Fullscreen Required</h2>
            <p style={{ textAlign: "center", marginBottom: "1.5rem" }}>
              This exam must be taken in fullscreen mode to maintain integrity.
              Please enter fullscreen to view the instructions.
            </p>
            <button className="exam-fs-enter-btn" onClick={requestFullscreen}>
              <i className="fa-solid fa-expand" style={{ marginRight: "0.5rem" }} />
              Enter Fullscreen
            </button>
          </div>
        </div>
      )}
      {/* ── Header (matches exam-header) ──────────────────────────────── */}
      <header className="ei-header">
        <div className="ei-header-left">
          <img src="/imgs/logo.svg" alt="grademe-logo" />
          <div className="ei-header-title">
            <span className="ei-header-testname">{test.testName}</span>
            <span className="ei-header-meta">
              {totalQuestions} Qs · {totalMarks} Marks{duration ? ` · ${duration} min` : ""}
            </span>
          </div>
        </div>

        <div className="ei-header-center">
          {countdown && !examStarted && (
            <div className="ei-timer-box">
              <i className="fa-solid fa-hourglass-half" />
              <span>{countdownText}</span>
            </div>
          )}
          {examStarted && (
            <div className="ei-timer-box started">
              <i className="fa-solid fa-circle-check" />
              <span>Exam Open</span>
            </div>
          )}
        </div>

        <div className="ei-header-right">
          {hasVideoProctoring ? (
            <div className="ei-proctored-badge" style={{ background: "rgba(239, 65, 54, 0.12)", color: "#ef4136" }}>
              <i className="fa-solid fa-video" />
              <span>Video Proctored</span>
            </div>
          ) : (
            <div className="ei-proctored-badge">
              <i className="fa-solid fa-shield-halved" />
              <span>Proctored</span>
            </div>
          )}
        </div>
      </header>

      {/* ── Step Tabs ──────────────────────────────────────────────── */}
      <div className="ei-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.num}
            type="button"
            className={`ei-tab ${currentStep === tab.num ? "active" : ""}`}
            onClick={() => setCurrentStep(tab.num)}
          >
            <i className={`fa-solid ${tab.icon}`} />
            <span className="ei-tab-label">{tab.label}</span>
            {tab.num === totalSteps && agreedToTerms && (
              <i className="fa-solid fa-circle-check ei-tab-check" />
            )}
          </button>
        ))}
      </div>

      {/* ── Content Area ──────────────────────────────────────────── */}
      <div className="ei-body">
        {/* ── Step 1: Exam Details ─────────────────────────────────── */}
        {currentStep === 1 && (
          <div className="ei-panel" key="s1">
            <div className="ei-panel-header">
              <i className="fa-solid fa-circle-info" />
              <h2>Exam Details</h2>
            </div>
            <div className="ei-details-grid">
              <div className="ei-detail-card">
                <i className="fa-solid fa-list-ol" />
                <div>
                  <span className="ei-detail-value">{totalQuestions}</span>
                  <span className="ei-detail-label">Questions</span>
                </div>
              </div>
              <div className="ei-detail-card">
                <i className="fa-solid fa-star" />
                <div>
                  <span className="ei-detail-value">{totalMarks}</span>
                  <span className="ei-detail-label">
                    {marksPerQuestion ? `Total Marks (${marksPerQuestion} Marks each)` : "Total Marks"}
                  </span>
                </div>
              </div>
              <div className="ei-detail-card">
                <i className="fa-solid fa-clock" />
                <div>
                  <span className="ei-detail-value">
                    {duration ? `${duration} min` : "No Limit"}
                  </span>
                  <span className="ei-detail-label">Duration</span>
                </div>
              </div>
              {test.scheduledDate && (
                <div className="ei-detail-card">
                  <i className="fa-solid fa-calendar-check" />
                  <div>
                    <span className="ei-detail-value ei-detail-value-sm">
                      {formatDate(test.scheduledDate)}
                    </span>
                    <span className="ei-detail-label">Scheduled Start</span>
                  </div>
                </div>
              )}
            </div>

            {/* ── Exam Configuration Tags ──────────────────────────── */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "1rem" }}>
              {isUdghosh && (
                <span className="ei-config-tag info">
                  <i className="fa-solid fa-circle-check" /> ONLY MCQ with One Correct Option
                </span>
              )}
              {isUdghosh && (
                <span className="ei-config-tag warning">
                  <i className="fa-solid fa-lock" /> 4 Locked Sections
                </span>
              )}
              {hasVideoProctoring && (
                <span className="ei-config-tag danger">
                  <i className="fa-solid fa-video" /> Video Proctored
                </span>
              )}
              {!isUdghosh && hasPerQuestionTimer && (
                <span className="ei-config-tag info">
                  <i className="fa-solid fa-stopwatch" /> Per-Question Timer
                </span>
              )}
              {!canNavigateBack && (
                <span className="ei-config-tag warning">
                  <i className="fa-solid fa-arrow-right" /> No Back Navigation
                </span>
              )}
            </div>

            {/* ── UDGHOSH Sections & Marking Scheme ────────────────── */}
            {isUdghosh && (
              <div className="ei-udghosh-sections-block">
                <div className="ei-sub-header">
                  <div className="ei-sub-header-icon">
                    <i className="fa-solid fa-layer-group" />
                  </div>
                  <div>
                    <h3>Sections & Marking Scheme</h3>
                    <p>
                      The examination contains 4 distinct sections. Please review the question distribution and marking criteria below:
                    </p>
                  </div>
                </div>

                <div className="ei-sections-grid">
                  {udghoshSections.map((sec, idx) => (
                    <div className="ei-section-card" key={idx}>
                      <div className="ei-section-card-top">
                        <div
                          className="ei-section-icon"
                          style={{
                            color: sec.accentColor,
                            background: `${sec.accentColor}18`,
                          }}
                        >
                          <i className={`fa-solid ${sec.icon}`} />
                        </div>
                        <div className="ei-section-info">
                          <span className="ei-section-badge">Section {idx + 1} · {sec.range}</span>
                          <h4 className="ei-section-name">{sec.name}</h4>
                        </div>
                      </div>

                      <div className="ei-section-meta-row">
                        <div className="ei-sec-stat">
                          <span className="ei-sec-stat-val">{sec.questions}</span>
                          <span className="ei-sec-stat-lbl">Questions</span>
                        </div>
                        <div className="ei-sec-stat-divider" />
                        <div className="ei-sec-stat">
                          <span className="ei-sec-stat-val">{sec.marks}</span>
                          <span className="ei-sec-stat-lbl">Total Marks</span>
                        </div>
                      </div>

                      <div className="ei-section-marking-row">
                        <div className="ei-mark-tag positive">
                          <i className="fa-solid fa-circle-check" />
                          <span><strong>{sec.correct}</strong> for correct option</span>
                        </div>
                        <div className="ei-mark-tag negative">
                          <i className="fa-solid fa-circle-minus" />
                          <span><strong>{sec.incorrect}</strong> for incorrect option</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {countdown && !examStarted && (
              <div className="ei-countdown-block" style={{ marginTop: isUdghosh ? "1.5rem" : "1rem" }}>
                <div className="ei-countdown-title">
                  <i className="fa-solid fa-hourglass-half" />
                  Exam Starts In
                </div>
                <div className="ei-countdown-units">
                  {countdown.days > 0 && (
                    <div className="ei-cd-unit">
                      <span className="ei-cd-val">{countdown.days}</span>
                      <span className="ei-cd-lbl">Days</span>
                    </div>
                  )}
                  <div className="ei-cd-unit">
                    <span className="ei-cd-val">{String(countdown.hours).padStart(2, "0")}</span>
                    <span className="ei-cd-lbl">Hours</span>
                  </div>
                  <div className="ei-cd-unit">
                    <span className="ei-cd-val">{String(countdown.minutes).padStart(2, "0")}</span>
                    <span className="ei-cd-lbl">Minutes</span>
                  </div>
                  <div className="ei-cd-unit">
                    <span className="ei-cd-val">{String(countdown.seconds).padStart(2, "0")}</span>
                    <span className="ei-cd-lbl">Seconds</span>
                  </div>
                </div>
              </div>
            )}

            {examStarted && (
              <div className="ei-ready-banner" style={{ marginTop: isUdghosh ? "1.5rem" : "1rem" }}>
                <i className="fa-solid fa-circle-check" />
                Exam is now open — proceed through all tabs, then start.
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: Question Status Guide ────────────────────────── */}
        {currentStep === 2 && (
          <div className="ei-panel" key="s2">
            <div className="ei-panel-header">
              <i className="fa-solid fa-palette" />
              <h2>Question Status Guide</h2>
            </div>
            <p className="ei-panel-desc">
              During the exam, each question is colour-coded in the palette
              to help you track progress:
            </p>
            <div className="ei-legend-grid">
              {[
                { cls: "answered", num: "1", title: "Answered", desc: "You selected an answer." },
                { cls: "not-answered", num: "2", title: "Not Answered", desc: "Visited but no answer selected." },
                { cls: "not-visited", num: "3", title: "Not Visited", desc: "Not yet opened." },
                ...(canNavigateBack ? [
                  { cls: "review", num: "4", title: "Marked for Review", desc: "Flagged to revisit later." },
                  { cls: "review-answered", num: "5", title: "Answered & Reviewed", desc: "Answered and also flagged." },
                ] : []),
              ].map((item) => (
                <div className="ei-legend-card" key={item.cls}>
                  <span className={`ei-legend-dot ${item.cls}`}>{item.num}</span>
                  <div className="ei-legend-info">
                    <strong>{item.title}</strong>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="ei-panel-header" style={{ marginTop: "1rem" }}>
              <i className="fa-solid fa-compass" />
              <h2>Navigation Guide</h2>
            </div>
            <div className="ei-nav-guide">
              {navGuideItems.map((btn) => (
                <div className="ei-nav-item" key={btn.cls}>
                  <div className={`ei-nav-btn-preview ${btn.cls}`}>
                    {btn.iconPos === "left" && <i className={`fa-solid ${btn.icon}`} />}
                    {btn.label}
                    {btn.iconPos === "right" && <i className={`fa-solid ${btn.icon}`} />}
                  </div>
                  <p>{btn.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 3: Rules (or Rules & Agreement for UDGHOSH) ──────── */}
        {currentStep === 3 && (
          <div className="ei-panel" key="s3">
            <div className="ei-panel-header">
              <i className="fa-solid fa-gavel" />
              <h2>{isUdghosh ? "Examination Rules & Regulations" : "Exam Rules & Instructions"}</h2>
            </div>

            {isUdghosh ? (
              <div className="ei-udghosh-rules-wrapper">
                <p className="ei-panel-desc">
                  Please read the following examination rules and regulations carefully. Compliance is mandatory for all candidates.
                </p>

                <div className="ei-rule-groups-list">
                  {udghoshRuleCategories.map((group) => (
                    <div className="ei-rule-group-card" key={group.id}>
                      <div className="ei-rule-group-header">
                        <div className={`ei-rule-group-icon ${group.colorClass}`}>
                          <i className={`fa-solid ${group.icon}`} />
                        </div>
                        <h3>{group.title}</h3>
                      </div>
                      <ul className="ei-rule-points">
                        {group.rules.map((ruleText, rIdx) => (
                          <li key={rIdx}>
                            <i className="fa-solid fa-chevron-right" />
                            <span>{ruleText}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Important Alert Box */}
                <div className="ei-important-box">
                  <div className="ei-important-box-header">
                    <i className="fa-solid fa-circle-exclamation" />
                    <strong>Important</strong>
                  </div>
                  <p>
                    Do not switch tabs, use unauthorised resources, or leave the examination portal during the exam. Please follow all examination rules carefully to avoid warnings, penalties, or disqualification.
                  </p>
                </div>

                {/* Integrated Acknowledgment Checkbox */}
                <div className="ei-agreement-box" style={{ marginTop: "2rem" }}>
                  <label className="ei-agree-label">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="ei-agree-checkbox"
                    />
                    <span className="ei-agree-checkmark">
                      <i className="fa-solid fa-check" />
                    </span>
                    <span className="ei-agree-text">
                      I have read and understood all the Examination Rules and Regulations.
                    </span>
                  </label>
                </div>
              </div>
            ) : (
              <div className="ei-rules-list">
                {rules.map((rule, i) => (
                  <div className="ei-rule-item" key={i}>
                    <div className={`ei-rule-icon ${rule.color}`}>
                      <i className={`fa-solid ${rule.icon}`} />
                    </div>
                    <div>
                      <strong>{rule.title}</strong>
                      <p>{rule.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Step 4: Agreement (Standard non-UDGHOSH only) ─────────── */}
        {!isUdghosh && currentStep === 4 && (
          <div className="ei-panel" key="s4">
            <div className="ei-panel-header">
              <i className="fa-solid fa-file-signature" />
              <h2>Declaration & Agreement</h2>
            </div>
            <div className="ei-agreement-box">
              <p>
                By checking the box below, you confirm that you have read and
                understood all instructions, rules, and guidelines provided in
                the previous tabs.
              </p>
              <label className="ei-agree-label">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="ei-agree-checkbox"
                />
                <span className="ei-agree-checkmark">
                  <i className="fa-solid fa-check" />
                </span>
                <span className="ei-agree-text">
                  I agree to abide by all rules. I will not engage in any form
                  of malpractice or unfair means. I understand that violations
                  will result in strict action including auto-submission with
                  zero marks.
                </span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* ── Bottom Bar ────────────────────────────────────────────── */}
      <div className="ei-bottom">
        <button
          type="button"
          className="ei-btn-back"
          onClick={handleGoBack}
        >
          <i className={`fa-solid ${currentStep === 1 ? "fa-arrow-left" : "fa-chevron-left"}`} />
          {currentStep === 1 ? "Go Back" : "Previous"}
        </button>

        <span className="ei-step-indicator">
          Step {currentStep} <span className="ei-step-of">of {totalSteps}</span>
        </span>

        {currentStep < totalSteps ? (
          <button
            type="button"
            className="ei-btn-next"
            onClick={() => setCurrentStep((p) => p + 1)}
          >
            Next <i className="fa-solid fa-chevron-right" />
          </button>
        ) : (
          <button
            type="button"
            className={`ei-btn-start ${canStart ? "ready" : ""}`}
            disabled={!canStart}
            onClick={handleStartExam}
          >
            {!examStarted ? (
              <><i className="fa-solid fa-lock" /> Waiting...</>
            ) : !agreedToTerms ? (
              <><i className="fa-solid fa-lock" /> Accept Terms</>
            ) : (
              <>Start Exam <i className="fa-solid fa-arrow-right" /></>
            )}
          </button>
        )}
      </div>

      {/* ── PIN Modal ─────────────────────────────────────────────── */}
      {showPinModal && (
        <div className="ei-pin-overlay">
          <div className="ei-pin-modal">
            <i className="fa-solid fa-lock ei-pin-icon" />
            <h2>Enter Exam PIN</h2>
            <p className="ei-pin-desc">
              Enter the 4-digit PIN provided by your instructor to start{" "}
              <strong>{test.testName}</strong>.
            </p>

            {pinError && (
              <div className="ei-pin-error">
                <i className="fa-solid fa-circle-exclamation" />
                {pinError}
              </div>
            )}

            <form onSubmit={handleVerifyPin}>
              <input
                type="text"
                value={examPin}
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                onChange={(e) =>
                  setExamPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))
                }
                placeholder="• • • •"
                className="ei-pin-input"
                style={{ WebkitTextSecurity: "disc" }}
                disabled={isVerifyingPin}
                autoFocus
              />
              <div className="ei-pin-actions">
                <button
                  type="button"
                  className="ei-btn-back"
                  onClick={() => setShowPinModal(false)}
                  disabled={isVerifyingPin}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`ei-btn-start ${examPin.length === 4 ? "ready" : ""}`}
                  disabled={isVerifyingPin || examPin.length !== 4}
                >
                  {isVerifyingPin ? (
                    <Loader
                      fontSize="1rem"
                      style={{ left: "0", top: "0", color: "#fff" }}
                    />
                  ) : (
                    <>Start Exam <i className="fa-solid fa-arrow-right" /></>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamInstructions;